//https://magicui.design/docs/components/highlighter

"use client"

import type React from "react"

import { Box } from "@chakra-ui/react"
import { useInView } from "motion/react"
import { useEffect, useRef } from "react"
import { annotate } from "rough-notation"
import { type RoughAnnotation } from "rough-notation/lib/model"

type AnnotationAction =
  | "highlight"
  | "underline"
  | "box"
  | "circle"
  | "strike-through"
  | "crossed-off"
  | "bracket"

interface HighlighterProps {
  children: React.ReactNode
  action?: AnnotationAction
  color?: string
  animationDelay?: number
  strokeWidth?: number
  animationDuration?: number
  iterations?: number
  padding?: number
  multiline?: boolean
  isView?: boolean
}

const TextHighlighter = ({
  action = "highlight",
  animationDelay = 0,
  animationDuration = 600,
  children,
  color = "#ffd1dc",
  isView = false,
  iterations = 2,
  multiline = true,
  padding = 2,
  strokeWidth = 1.5,
}: HighlighterProps) => {
  const elementRef = useRef<HTMLSpanElement>(null)
  const annotationRef = useRef<RoughAnnotation | null>(null)

  const isInView = useInView(elementRef, {
    margin: "-10%",
    once: true,
  })

  // If isView is false, always show. If isView is true, wait for inView
  const shouldShow = !isView || isInView

  useEffect(() => {
    if (!shouldShow) return

    const element = elementRef.current
    if (!element) return
    let didShow = false
    let frameId = 0
    let timeoutId: number | undefined

    const annotationConfig = {
      animationDuration,
      color,
      iterations,
      multiline,
      padding,
      strokeWidth,
      type: action,
    }

    const annotation = annotate(element, annotationConfig)

    annotationRef.current = annotation

    const showAnnotation = () => {
      didShow = true
      annotationRef.current?.show()
    }

    if (animationDelay > 0) {
      timeoutId = window.setTimeout(showAnnotation, animationDelay)
    } else {
      showAnnotation()
    }

    const redrawAnnotation = () => {
      if (!didShow) return

      cancelAnimationFrame(frameId)
      frameId = window.requestAnimationFrame(() => {
        annotation.hide()
        annotation.show()
      })
    }

    const resizeObserver = new ResizeObserver(redrawAnnotation)
    resizeObserver.observe(element)
    window.addEventListener("resize", redrawAnnotation)

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
      }
      cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
      window.removeEventListener("resize", redrawAnnotation)
      annotation.remove()
    }
  }, [
    shouldShow,
    action,
    animationDelay,
    color,
    strokeWidth,
    animationDuration,
    iterations,
    padding,
    multiline,
  ])

  return (
    <Box
      as="span"
      bgColor={"transparent"}
      className="TextHighligher"
      display={"inline-block"}
      position="relative"
      ref={elementRef}
    >
      {children}
    </Box>
  )
}

export default TextHighlighter
